import { call, delay, put, race, takeEvery } from "typed-redux-saga";
import { Action } from "@reduxjs/toolkit";
import ConfigurationClient from "../../web-apis/configuration-client";
import { MVisionAppClient } from "../configurationTarget/mvision-client-list";
import { ModelType } from "../global-types/customization-types";
import { BackendValidationError, getApiErrorMessage } from "../../util/errors";
import { callApi } from "../sagas";
import { backupListFetched, backupListFetchRequested, backupListFetchStarted, backupRestoreFinished, backupRestoreRequested, backupRestoreStarted, listModelTypeSet, restoreModelTypeSet } from "./backupSlice";
import { ContouringCustomizationEntitiesForExport } from "../contouring/contouring-types";
import { DoseCustomizationEntitiesForExport } from "../dose/dose-types";
import { ImageCustomizationEntitiesForExport } from "../image/image-types";
import { AdaptCustomizationEntitiesForExport } from "../adapt/adapt-types";
import { collectContouringCustomizationForExport, fetchContouringCustomizationsSaga } from "../contouring/contouringSagas";
import { collectDoseCustomizationForExport, fetchDoseCustomizationsSaga } from "../dose/doseSagas";
import { collectImageCustomizationForExport, fetchImageCustomizationsSaga } from "../image/imageSagas";
import { collectAdaptCustomizationForExport, fetchAdaptCustomizationsSaga } from "../adapt/adaptSagas";

function* fetchBackupListSaga(action: Action) {
    if (!backupListFetchRequested.match(action)) return;

    const { appClient, modelType } = action.payload;
    yield* put(listModelTypeSet(modelType));
    yield* put(backupListFetchStarted());

    yield* callApi({
        doApiCall: function* (client: ConfigurationClient) {
            const backups = yield* call(
                async () => client.fetchBackupsAsync(appClient, modelType)
            );
            return backups;
        },
        onFinish: function* (result, error) {
            if (error || !result) {
                const errorMessage = getApiErrorMessage(error, 'Failed to load backups.');
                yield* put(backupListFetched({ backups: [], modelType, error: errorMessage }));
                return false;
            }
            yield* put(backupListFetched({ backups: result, modelType }));
            return true;
        },
    });
}

/** Download a backup and save it via the normal pipeline for the given model type. */
async function downloadAndSaveBackup(client: ConfigurationClient, appClient: MVisionAppClient, modelType: ModelType, backupFilename: string): Promise<boolean> {
    switch (modelType) {
        case ModelType.Contouring: {
            const viewModels = await client.downloadContouringBackupAsync(appClient, backupFilename);
            return (await client.saveContouringCustomizationAsync(appClient, viewModels, true)).success;
        }
        case ModelType.Dose: {
            const viewModels = await client.downloadDoseBackupAsync(appClient, backupFilename);
            return (await client.saveDoseCustomizationAsync(appClient, viewModels, true)).success;
        }
        case ModelType.Image: {
            const viewModels = await client.downloadImageBackupAsync(appClient, backupFilename);
            return (await client.saveImageCustomizationAsync(appClient, viewModels, true)).success;
        }
        case ModelType.Adapt: {
            const viewModels = await client.downloadAdaptBackupAsync(appClient, backupFilename);
            return (await client.saveAdaptCustomizationAsync(appClient, viewModels, true)).success;
        }
        default:
            throw new Error(`Unsupported model type for backup restore: ${modelType}`);
    }
}

type RollbackData =
    | { modelType: ModelType.Contouring; customization: ContouringCustomizationEntitiesForExport }
    | { modelType: ModelType.Dose; customization: DoseCustomizationEntitiesForExport }
    | { modelType: ModelType.Image; customization: ImageCustomizationEntitiesForExport }
    | { modelType: ModelType.Adapt; customization: AdaptCustomizationEntitiesForExport };

/** Collect current config from Redux store for rollback purposes. */
function* collectCustomizationForRollback(modelType: ModelType): Generator<unknown, RollbackData, unknown> {
    switch (modelType) {
        case ModelType.Contouring:
            return { modelType, customization: yield* collectContouringCustomizationForExport() };
        case ModelType.Dose:
            return { modelType, customization: yield* collectDoseCustomizationForExport() };
        case ModelType.Image:
            return { modelType, customization: yield* collectImageCustomizationForExport() };
        case ModelType.Adapt:
            return { modelType, customization: yield* collectAdaptCustomizationForExport() };
        default:
            throw new Error(`Unsupported model type for rollback: ${modelType}`);
    }
}

/** Save previously collected customization data back to the backend for rollback. */
async function saveRollbackData(client: ConfigurationClient, appClient: MVisionAppClient, rollback: RollbackData): Promise<boolean> {
    switch (rollback.modelType) {
        case ModelType.Contouring: return (await client.saveContouringCustomizationAsync(appClient, rollback.customization, true)).success;
        case ModelType.Dose: return (await client.saveDoseCustomizationAsync(appClient, rollback.customization, true)).success;
        case ModelType.Image: return (await client.saveImageCustomizationAsync(appClient, rollback.customization, true)).success;
        case ModelType.Adapt: return (await client.saveAdaptCustomizationAsync(appClient, rollback.customization, true)).success;
        default: {
            const exhaustiveCheck: never = rollback;
            throw new Error(`Unsupported model type for rollback: ${(exhaustiveCheck as RollbackData).modelType}`);
        }
    }
}

function* restoreBackupSaga(action: Action) {
    if (!backupRestoreRequested.match(action) || !action.payload) return;

    const { appClient, backupFilename, modelType } = action.payload;
    yield* put(restoreModelTypeSet(modelType));
    yield* put(backupRestoreStarted());

    // Collect current config for rollback before attempting restore.
    // Wrapped in try-catch so a failure here doesn't kill the takeEvery watcher.
    let rollbackData: RollbackData | null = null;
    try {
        rollbackData = yield* collectCustomizationForRollback(modelType);
    } catch (err) {
        console.error('Failed to collect rollback data, proceeding without rollback support:', err);
    }

    yield* callApi({
        doApiCall: function* (client: ConfigurationClient) {
            const RESTORE_TIMEOUT_MS = 30_000;

            const { success, timeout } = yield* race({
                success: call(async () => downloadAndSaveBackup(client, appClient, modelType, backupFilename)),
                timeout: delay(RESTORE_TIMEOUT_MS),
            });

            if (timeout !== undefined) {
                throw new Error(`Backup restore timed out after ${Math.round(RESTORE_TIMEOUT_MS / 1000)} seconds.`);
            }

            if (!success) {
                throw new Error('Failed to save backup configuration to backend');
            }

            return true;
        },
        onFinish: function* (_result: boolean | undefined, error: Error | BackendValidationError | null) {
            if (error) {
                const errorMessage = getApiErrorMessage(error, 'Failed to restore backup.');
                yield* put(backupRestoreFinished({ success: false, errorMessage }));

                // Attempt rollback as best-effort after notifying the UI
                if (rollbackData) {
                    yield* callApi({
                        doApiCall: function* (client: ConfigurationClient) {
                            return yield* call(
                                async () => saveRollbackData(client, appClient, rollbackData!)
                            );
                        },
                        // eslint-disable-next-line require-yield
                        onFinish: function* (_rollbackResult, rollbackError) {
                            if (rollbackError) {
                                console.error('Rollback after failed backup restore also failed:', rollbackError);
                            }
                            return !rollbackError;
                        },
                    });
                }

                return false;
            }

            // Re-fetch customizations for the restored model type to update the Redux store
            switch (modelType) {
                case ModelType.Contouring: yield* call(fetchContouringCustomizationsSaga, appClient); break;
                case ModelType.Dose: yield* call(fetchDoseCustomizationsSaga, appClient); break;
                case ModelType.Image: yield* call(fetchImageCustomizationsSaga, appClient); break;
                case ModelType.Adapt: yield* call(fetchAdaptCustomizationsSaga, appClient); break;
            }
            yield* put(backupRestoreFinished({ success: true }));
            return true;
        },
    });
}

export function* watchFetchBackupListSaga() {
    yield* takeEvery(backupListFetchRequested, fetchBackupListSaga);
}

export function* watchRestoreBackupSaga() {
    yield* takeEvery(backupRestoreRequested, restoreBackupSaga);
}

export function getWatchesForBackupSagas() {
    return [
        watchFetchBackupListSaga(),
        watchRestoreBackupSaga(),
    ];
}
